import {
  ALL_REALS,
  flipRelation,
  type IntervalSet,
  intersectSets,
  NO_REALS,
  type Relation,
  rayFor,
} from "@/lib/tool-kit/algebra/intervals";
import {
  type Uni,
  uniCoefficient,
  uniCommonDenominator,
  uniDegree,
  uniScale,
  uniSub,
  uniTrim,
} from "@/lib/tool-kit/algebra/uni";
import {
  compareR,
  divR,
  fromBigInt,
  intR,
  isZeroR,
  type Rational,
  signR,
} from "@/lib/tool-kit/math/rational";

/**
 * Solving a linear equation or inequality the way a teacher writes it on the board.
 *
 * The statement is a chain of polynomial parts joined by relations: `3x − 2 = 2x + 7`
 * is two parts and one `=`; `−3 < 2x + 1 ≤ 7` is three parts and two relations. Each
 * step does the same thing to every part (add, subtract, multiply, divide), so the
 * chain stays true, and the engine records the new chain after each step. Dividing or
 * multiplying by a negative number reverses every inequality sign — the step says so.
 */
export type StepKind =
  | "simplify"
  | "clear"
  | "move-variable"
  | "move-constant"
  | "divide"
  | "reverse";

export interface RelationStep {
  kind: StepKind;
  parts: Uni[];
  rels: Relation[];
  /** The number added, subtracted, multiplied by or divided by (signed). */
  amount?: Rational;
  /** The power of the variable moved, for `move-variable`. */
  degree?: number;
  /** An inequality sign was reversed in this step. */
  flipped?: boolean;
}

export type RelationOutcome =
  | { kind: "solved" }
  | { kind: "identity" }
  | { kind: "contradiction" }
  | { kind: "nonlinear"; degree: number }
  | { kind: "outside" };

export interface RelationSolution {
  steps: RelationStep[];
  outcome: RelationOutcome;
  /** For `solved`: the solution value (an equation) or set (an inequality). */
  value: Rational | null;
  set: IntervalSet;
}

function holds(rel: Relation, left: Rational, right: Rational): boolean {
  const order = compareR(left, right);
  switch (rel) {
    case "<":
      return order < 0;
    case "≤":
      return order <= 0;
    case ">":
      return order > 0;
    case "≥":
      return order >= 0;
    default:
      return order === 0;
  }
}

function constantsHold(parts: Uni[], rels: Relation[]): boolean {
  return rels.every((rel, i) =>
    holds(rel, uniCoefficient(parts[i], 0), uniCoefficient(parts[i + 1], 0))
  );
}

function term(coefficient: Rational, degree: number): Uni {
  const result: Uni = Array.from({ length: degree }, () => intR(0));
  result.push(coefficient);
  return result;
}

function finalSet(parts: Uni[], rels: Relation[], pivot: number): IntervalSet {
  let set = ALL_REALS;
  rels.forEach((rel, i) => {
    // rel sits between parts[i] and parts[i + 1]; one of the two is the pivot x.
    if (i === pivot) {
      set = intersectSets(set, rayFor(rel, uniCoefficient(parts[i + 1], 0)));
    } else if (i + 1 === pivot) {
      set = intersectSets(
        set,
        rayFor(flipRelation(rel), uniCoefficient(parts[i], 0))
      );
    }
  });
  return set;
}

interface Chain {
  parts: Uni[];
  rels: Relation[];
}

function applyToAll(
  chain: Chain,
  operation: (part: Uni) => Uni,
  rels: Relation[] = chain.rels
): Chain {
  return { parts: chain.parts.map(operation).map(uniTrim), rels };
}

interface Work {
  chain: Chain;
  steps: RelationStep[];
}

function record(work: Work, extra: Omit<RelationStep, "parts" | "rels">): void {
  work.steps.push({ ...extra, parts: work.chain.parts, rels: work.chain.rels });
}

function clearFractions(work: Work): void {
  const denominator = uniCommonDenominator(work.chain.parts);
  if (denominator > BigInt(1)) {
    const factor = fromBigInt(denominator);
    work.chain = applyToAll(work.chain, (p) => uniScale(p, factor));
    record(work, { kind: "clear", amount: factor });
  }
}

/** Two-sided statements: move every variable term to the left, highest power first. */
function moveVariables(work: Work): void {
  const [left, right] = work.chain.parts;
  const top = Math.max(uniDegree(left), uniDegree(right));
  for (let degree = top; degree >= 1; degree -= 1) {
    const c = uniCoefficient(work.chain.parts[1], degree);
    if (!isZeroR(c)) {
      const moved = term(c, degree);
      work.chain = applyToAll(work.chain, (p) => uniSub(p, moved));
      record(work, { kind: "move-variable", amount: c, degree });
    }
  }
}

function divideThrough(work: Work, a: Rational, inequality: boolean): void {
  const flips = inequality && signR(a) < 0;
  const reciprocal = divR(intR(1), a);
  work.chain = applyToAll(
    work.chain,
    (p) => uniScale(p, reciprocal),
    flips ? work.chain.rels.map(flipRelation) : work.chain.rels
  );
  record(work, { kind: "divide", amount: a, flipped: flips });
  if (flips && work.chain.parts.length === 3) {
    work.chain = {
      parts: [...work.chain.parts].reverse(),
      rels: [...work.chain.rels].reverse().map(flipRelation),
    };
    record(work, { kind: "reverse" });
  }
}

const ONE = intR(1);

function isOne(a: Rational): boolean {
  return a.num === ONE.num && a.den === ONE.den;
}

/** Why the statement cannot be solved as a linear one, or null when it can. */
function obstacle(parts: Uni[]): RelationOutcome | null {
  // The variable must live in the middle part alone for a double inequality.
  if (
    parts.length === 3 &&
    (uniDegree(parts[0]) > 0 || uniDegree(parts[2]) > 0)
  ) {
    return { kind: "outside" };
  }
  const net = parts.length === 2 ? uniSub(parts[0], parts[1]) : parts[1];
  return uniDegree(net) > 1
    ? { kind: "nonlinear", degree: uniDegree(net) }
    : null;
}

/**
 * Solve a one-variable linear statement. `parts` has two entries (left and right) or
 * three (a double inequality with the variable in the middle); `rels` is one shorter.
 */
export function solveRelation(
  parts: Uni[],
  rels: Relation[]
): RelationSolution {
  const trimmed = parts.map(uniTrim);
  const pivot = trimmed.length === 3 ? 1 : 0;
  const inequality = rels.some((rel) => rel !== "=");
  const work: Work = {
    chain: { parts: trimmed, rels },
    steps: [{ kind: "simplify", parts: trimmed, rels }],
  };
  const stop = (outcome: RelationOutcome): RelationSolution => ({
    steps: work.steps,
    outcome,
    value: null,
    set: NO_REALS,
  });

  const blocked = obstacle(trimmed);
  if (blocked) {
    return stop(blocked);
  }
  clearFractions(work);
  if (trimmed.length === 2) {
    moveVariables(work);
  }

  const a = uniCoefficient(work.chain.parts[pivot], 1);
  if (isZeroR(a)) {
    const true_ = constantsHold(work.chain.parts, work.chain.rels);
    return stop({ kind: true_ ? "identity" : "contradiction" });
  }
  const b = uniCoefficient(work.chain.parts[pivot], 0);
  if (!isZeroR(b)) {
    work.chain = applyToAll(work.chain, (p) => uniSub(p, [b]));
    record(work, { kind: "move-constant", amount: b });
  }
  if (!isOne(a)) {
    divideThrough(work, a, inequality);
  }

  const { parts: done, rels: doneRels } = work.chain;
  return {
    steps: work.steps,
    outcome: { kind: "solved" },
    value: inequality ? null : uniCoefficient(done[1], 0),
    set: finalSet(done, doneRels, pivot),
  };
}
